import { lazy, Suspense, type ReactNode } from "react";
import { useT } from "../i18n/catalogue.js";

import { EmptyState, LoadingState } from "../states/index.js";
import { isEmpty, type ChartSeries } from "./series.js";
import type { ChartMarker, ChartVariant } from "./StackedAreaChartImpl.js";

/**
 * A stacked area chart over monthly buckets — the only chart shape v1 uses.
 *
 * THE SPLIT IS INSIDE THE COMPONENT, ON PURPOSE.
 *
 * Recharts brings react-redux, @reduxjs/toolkit, immer, victory-vendor and more. Left in the main
 * bundle it would put the whole library on the sign-in screen, which draws no charts at all. This
 * repository has just finished splitting the WebGL background out for exactly that reason.
 *
 * Doing it here rather than asking each consumer to `lazy()` its own import is the same argument as
 * the rest of feature 004: a rule every caller must remember is a rule that will be forgotten, and
 * the failure — a bundle quietly growing by 200kB — is one nobody notices for months.
 *
 * `lazy` at MODULE SCOPE, not inside the component. Calling it per render creates a new lazy
 * component every pass, which remounts the chart and refetches the chunk. Learned from
 * `AppBackground` in apps/app, which carries the same note.
 */
const Impl = lazy(() =>
  import("./StackedAreaChartImpl.js").then((m) => ({ default: m.StackedAreaChartImpl })),
);

export interface StackedAreaChartProps {
  series: ChartSeries[];
  /** Vertical marks with a reason — see `ChartMarker`. */
  markers?: ChartMarker[];
  /**
   * `stacked` composes — the top of the stack is the whole, which is what net worth wants.
   * `lines` compares — each series is drawn from zero, so its height is its own value.
   *
   * Savings uses `lines`: a reader looking at a list of accounts is asking how each one is doing,
   * and a stack answers a different question while looking like it answers that one.
   */
  variant?: ChartVariant;
  /**
   * Draw the sum of the layers as a line over the stack, under this label.
   *
   * FOR A STACK WHOSE LAYERS ARE NOT ALL POSITIVE, in which case the sum is drawn nowhere: the top
   * of the stack is the sum of the positive layers only. 013's net worth chart is that case — assets
   * above the axis, the mortgage below it — and its total was absent from the plot until this
   * existed.
   *
   * Ignored by `lines`, where every series is already drawn from zero and a total would be a fifth
   * line among four rather than the thing they compose into.
   */
  total?: { label: string };
  /**
   * Draw the built-in legend. Default true.
   *
   * `false` for a consumer that lists the series itself with more than a colour and a name — 013's net
   * worth chart names each pillar with its figure and its share of assets beneath the plot, and two
   * legends in one card is duplication a reader has to reconcile.
   */
  legend?: boolean;
  /** Shown when there is nothing to draw. Callers know what "nothing" means here; the chart does not. */
  emptyMessage?: ReactNode;
}

export function StackedAreaChart({
  series,
  markers,
  variant = "stacked",
  total,
  legend = true,
  emptyMessage,
}: StackedAreaChartProps): ReactNode {
  const t = useT();
  /*
   * Checked BEFORE the boundary, so an empty chart never downloads the library. A household with no
   * savings accounts is the common case on a fresh install, and it should not pay for a chart it
   * cannot draw.
   */
  if (isEmpty(series)) return <EmptyState>{emptyMessage ?? t("ui.chart.empty")}</EmptyState>;

  return (
    <Suspense fallback={<LoadingState>{t("ui.chart.loading")}</LoadingState>}>
      <Impl
        series={series}
        variant={variant}
        markers={markers ?? []}
        {...(variant === "lines" || total === undefined ? {} : { total })}
        legend={legend}
      />
    </Suspense>
  );
}
